/**
 * Monitoring Anomaly Models
 *
 * This file contains all TypeScript interfaces and enums for tracking
 * monitoring system anomalies and blockages.
 */
import { BrowserInfo } from '@fantaz/shared/data-access/api';

/**
 * Enum for anomaly codes to categorize different types of monitoring issues
 */
export enum MonitoringAnomalyCode {
  // GTM Related
  GTM_BLOCKED = 'GTM_BLOCKED',
  GTM_LOAD_FAILED = 'GTM_LOAD_FAILED',
  GTM_NOT_INITIALIZED = 'GTM_NOT_INITIALIZED',

  // Smartlook Related
  SMARTLOOK_BLOCKED = 'SMARTLOOK_BLOCKED',
  SMARTLOOK_RECORDING_DISABLED = 'SMARTLOOK_RECORDING_DISABLED',
  SMARTLOOK_NOT_INTEGRATED = 'SMARTLOOK_NOT_INTEGRATED',

  // PostHog Related
  POSTHOG_BLOCKED = 'POSTHOG_BLOCKED',
  POSTHOG_NOT_LOADED = 'POSTHOG_NOT_LOADED',
  POSTHOG_NOT_INITIALIZED = 'POSTHOG_NOT_INITIALIZED',

  // Clarity Related
  CLARITY_BLOCKED = 'CLARITY_BLOCKED',
  CLARITY_NOT_LOADED = 'CLARITY_NOT_LOADED',

  // Multiple systems
  MULTIPLE_SYSTEMS_BLOCKED = 'MULTIPLE_SYSTEMS_BLOCKED',
  ALL_SYSTEMS_BLOCKED = 'ALL_SYSTEMS_BLOCKED',

  // User actions
  USER_USING_AD_BLOCKER = 'USER_USING_AD_BLOCKER',
  USER_PRIVACY_EXTENSION_DETECTED = 'USER_PRIVACY_EXTENSION_DETECTED',
}

/**
 * Severity levels for monitoring anomalies
 */
export enum MonitoringAnomalySeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

/**
 * Enum for monitoring system failure reasons
 */
export enum MonitoringFailureReason {
  BLOCKED = 'BLOCKED', // Blocked by ad blocker or privacy extension
  OFFLINE = 'OFFLINE', // Service is offline/unavailable
  NO_CONNECTION = 'NO_CONNECTION', // User has no internet connection
  NOT_LOADED = 'NOT_LOADED', // Script failed to load
  NOT_INITIALIZED = 'NOT_INITIALIZED', // Script loaded but not initialized
  UNKNOWN = 'UNKNOWN', // Unknown failure reason
}

/**
 * Interface for monitoring system status
 */
export interface MonitoringSystemInfo {
  name: string;
  isActive: boolean;
  lastChecked: number;
  errorMessage?: string;
  failureReason?: MonitoringFailureReason;
}

/**
 * Interface for monitoring blocked status (object format)
 */
export interface MonitoringBlockedStatus {
  gtmBlocked: boolean;
  smartlookBlocked: boolean;
  posthogBlocked: boolean;
  clarityBlocked: boolean;
}

/**
 * Interface for a monitoring anomaly record
 */
export interface MonitoringAnomaly {
  uuid?: string;
  userId?: string;
  username?: string;
  playInstanceUuid?: string;
  eventPrototypeUuid?: string;
  eventScheduleUuid?: string;
  anomalyCode: MonitoringAnomalyCode;
  severity: MonitoringAnomalySeverity;
  blockedSystems: string[];
  systemsStatus: {
    gtm: boolean;
    smartlook: boolean;
    posthog: boolean;
    clarity: boolean;
  };
  detectedAt: number;
  userAgent?: string;
  browserInfo?: BrowserInfo;
  gameKey?: string;
  tournamentName?: string;
  metadata?: Record<string, any>;
}

/**
 * Interface for anomaly tracking request
 */
export interface MonitoringAnomalyRequest {
  anomaly: MonitoringAnomaly;
  sendSlackNotification?: boolean;
  sendEmailNotification?: boolean;
}

/**
 * Interface for anomaly tracking response
 */
export interface MonitoringAnomalyResponse {
  success: boolean;
  anomalyUuid: string;
  message?: string;
  timestamp: number;
}

/**
 * Interface for anomaly statistics
 */
export interface MonitoringAnomalyStats {
  totalAnomalies: number;
  byCode: Record<MonitoringAnomalyCode, number>;
  bySeverity: Record<MonitoringAnomalySeverity, number>;
  bySystem: {
    gtm: number;
    smartlook: number;
    posthog: number;
    clarity: number;
  };
  timeRange: {
    start: number;
    end: number;
  };
}

/**
 * Interface for user notification when monitoring is blocked
 */
export interface MonitoringBlockedNotification {
  show: boolean;
  blockedSystems: string[];
  message: string;
  allowPlay: boolean;
  canDismiss: boolean;
}

/**
 * Helper function to determine severity based on blocked systems
 */
export function determineAnomalySeverity(
  blockedSystems: string[],
): MonitoringAnomalySeverity {
  const blockedCount = blockedSystems.length;

  if (blockedCount === 0) {
    return MonitoringAnomalySeverity.LOW;
  } else if (blockedCount === 1) {
    return MonitoringAnomalySeverity.MEDIUM;
  } else if (blockedCount >= 2 && blockedCount < 4) {
    return MonitoringAnomalySeverity.HIGH;
  } else {
    return MonitoringAnomalySeverity.CRITICAL;
  }
}

/**
 * Helper function to determine anomaly code based on blocked systems
 */
export function determineAnomalyCode(
  blockedSystems: string[],
): MonitoringAnomalyCode {
  const blockedCount = blockedSystems.length;

  if (blockedCount === 0) {
    return MonitoringAnomalyCode.GTM_NOT_INITIALIZED; // Default
  }

  if (blockedCount === 4) {
    return MonitoringAnomalyCode.ALL_SYSTEMS_BLOCKED;
  }

  if (blockedCount > 1) {
    return MonitoringAnomalyCode.MULTIPLE_SYSTEMS_BLOCKED;
  }

  // Single system blocked
  const system = blockedSystems[0].toLowerCase();
  if (system.includes('gtm')) {
    return MonitoringAnomalyCode.GTM_BLOCKED;
  }
  if (system.includes('smartlook')) {
    return MonitoringAnomalyCode.SMARTLOOK_BLOCKED;
  }
  if (system.includes('posthog')) {
    return MonitoringAnomalyCode.POSTHOG_BLOCKED;
  }
  if (system.includes('clarity')) {
    return MonitoringAnomalyCode.CLARITY_BLOCKED;
  }

  return MonitoringAnomalyCode.USER_USING_AD_BLOCKER;
}

/**
 * Helper function to convert MonitoringBlockedStatus object to array of blocked system names
 */
export function getBlockedSystemNames(
  blockedStatus: MonitoringBlockedStatus,
): string[] {
  const blocked: string[] = [];
  if (blockedStatus.gtmBlocked) {
    blocked.push('GTM');
  }
  if (blockedStatus.smartlookBlocked) {
    blocked.push('Smartlook');
  }
  if (blockedStatus.posthogBlocked) {
    blocked.push('PostHog');
  }
  if (blockedStatus.clarityBlocked) {
    blocked.push('Clarity');
  }
  return blocked;
}

/**
 * Helper function to check if any monitoring system is blocked
 */
export function hasBlockedSystems(
  blockedStatus: MonitoringBlockedStatus,
): boolean {
  return (
    blockedStatus.gtmBlocked ||
    blockedStatus.smartlookBlocked ||
    blockedStatus.posthogBlocked ||
    blockedStatus.clarityBlocked
  );
}

/**
 * Helper function to count how many monitoring systems are blocked
 */
export function getBlockedSystemCount(
  blockedStatus: MonitoringBlockedStatus,
): number {
  return getBlockedSystemNames(blockedStatus).length;
}

/**
 * Helper function to get user-friendly message for blocked monitoring
 * Accepts both string array and MonitoringBlockedStatus object formats
 */
export function getBlockedMonitoringMessage(
  blockedSystems: string[] | MonitoringBlockedStatus,
): string {
  // Convert to array if object format
  const systemsArray = Array.isArray(blockedSystems)
    ? blockedSystems
    : getBlockedSystemNames(blockedSystems);

  if (systemsArray.length === 0) {
    return 'All monitoring systems are active.';
  }

  const systemsList = systemsArray.join(', ');

  return `Please disable ad blockers or whitelist our site. Our monitoring (${systemsList}) is essential for fair play and game integrity, not for ads or data selling.`;
}
